import type { FC, FocusEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { getItemFromLocalStorage, selectCouponSuggestedAmounts, setItemToLocalStorage, useToggle } from '@mwl/core-lib';
import CheckIcon from '@public/assets/common/icons/doubleCheck.svg';
import EditIcon from '@public/assets/common/icons/edit.svg';
import { Button } from '@/components/Button/Button';
import { useTypedSelector } from '@/hooks';
import { CouponSumBadge } from '../CouponSumBadge/CouponSumBadge';
import type { CouponSumBadgesProps, CouponSumBadgesValues } from './CouponSumBadges.types';
import styles from './CouponSumBadges.module.scss';
const maxLength = 9;
const storageKey = 'couponBadges';
const defaultValues: CouponSumBadgesValues = ['100', '200', '300'];
const CouponSumBadges: FC<CouponSumBadgesProps> = ({
  onBadgeClick
}) => {
  const [sortedBadgeValues, setSortedBadgeValues] = useState<CouponSumBadgesValues | []>([]);
  const [couponBadgeSums, setCouponBadgeSums] = useState<CouponSumBadgesValues | []>(sortedBadgeValues);
  const [isEditable, toggleIsEditable] = useToggle(false);
  const suggestedAmounts = useTypedSelector(selectCouponSuggestedAmounts);
  const handleBadgeSumChange = useCallback((value: string, index: number) => {
    setCouponBadgeSums(prev => {
      const newValues = [...prev];
      newValues[index] = value.slice(-maxLength);
      return (newValues as CouponSumBadgesValues);
    });
  }, []);
  const setNewSortedValues = () => {
    const newValues = ([...couponBadgeSums].sort((a, b) => +a - +b) as CouponSumBadgesValues);
    setSortedBadgeValues(newValues);
    setItemToLocalStorage<CouponSumBadgesValues>(storageKey, newValues);
  };
  const handleToggleEdit = () => {
    if (!isEditable) {
      setCouponBadgeSums([...sortedBadgeValues]);
    } else {
      setNewSortedValues();
    }
    toggleIsEditable();
  };
  const handleOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget == null || !event.currentTarget.contains(event.relatedTarget)) {
      setTimeout(() => toggleIsEditable(false), 0);
    }
  };
  const renderItems = isEditable ? couponBadgeSums : sortedBadgeValues;
  const getInitiateBadgesState = useCallback(() => {
    const localStorageBadges = getItemFromLocalStorage<CouponSumBadgesValues>(storageKey);
    if (localStorageBadges) {
      setSortedBadgeValues(localStorageBadges);
      return;
    }
    if (suggestedAmounts) {
      setSortedBadgeValues([suggestedAmounts?.minAmount?.toString(), suggestedAmounts?.midAmount?.toString(), suggestedAmounts?.maxAmount?.toString()]);
      return;
    }
    setSortedBadgeValues(defaultValues);
  }, [suggestedAmounts]);
  useEffect(() => {
    getInitiateBadgesState();
  }, [getInitiateBadgesState]);
  return <div className={cn(styles.root, {
    [styles.editable]: isEditable
  })} onBlur={handleOnBlur} data-sentry-component="CouponSumBadges" data-sentry-source-file="CouponSumBadges.tsx">
      {renderItems?.map((badgeSum, index) => {
      return <CouponSumBadge key={index} inputDisabled={!isEditable} value={badgeSum} onChange={value => {
        handleBadgeSumChange(value, index);
      }} className={styles.badge} onClick={onBadgeClick} />;
    })}
      {isEditable ? <Button as="button" className={styles.okButton} onClick={handleToggleEdit} variant="text" tabIndex={0}>
          <CheckIcon className={styles.editIcon} />
        </Button> : <Button as="button" className={styles.editButton} onClick={handleToggleEdit} rounded cut={false} variant="filled" color="black-light" tabIndex={0}>
          <EditIcon className={styles.editIcon} />
        </Button>}
    </div>;
};
export { CouponSumBadges };